import React,{useEffect,useMemo,useState} from "react";
import {useNavigate} from "react-router-dom";
import {toast} from "react-toastify";
import {getBranches,getDashboardSummary} from "./dashboardService";
import "./Dashboards.css";

const money=(value,currency)=>`${currency?.symbol||currency?.code||""}${Number(value||0).toLocaleString(undefined,{
 minimumFractionDigits:2,
 maximumFractionDigits:2,
})}`;

const greeting=()=>{
 const hour=new Date().getHours();
 if(hour<12)return "Good morning";
 if(hour<18)return "Good afternoon";
 return "Good evening";
};

const icons={
 PAYMENT:"bi-cash-stack",
 PROPOSAL:"bi-file-earmark-text",
 CLAIM:"bi-heart-pulse",
 POLICY:"bi-shield-check",
};

function RingChart({title,rows,onClick}){
 const total=rows.reduce((sum,row)=>sum+Number(row.value||0),0);
 const active=rows.find(row=>row.value>0)||rows[0];
 return <section className="executive-panel ring-panel">
  <header><div><span>PORTFOLIO VIEW</span><h3>{title}</h3></div></header>
  <div className="ring-content">
   <button className="ring-visual" onClick={onClick}>
    <div><strong>{total}</strong><span>Total</span></div>
   </button>
   <div className="ring-legend">
    {rows.map(row=><button key={row.label} onClick={onClick}>
     <i/><span>{row.label}</span><strong>{row.value}</strong>
    </button>)}
   </div>
  </div>
 </section>;
}

function BarChart({rows,currency}){
 const max=Math.max(1,...rows.map(row=>Number(row.amount||0)));
 return <div className="executive-bar-chart">
  {rows.map(row=><div key={row.month}>
   <span>{money(row.amount,currency)}</span>
   <div><i style={{height:`${Math.max(6,(Number(row.amount||0)/max)*100)}%`}}/></div>
   <small>{row.month}</small>
  </div>)}
 </div>;
}

function Kpi({label,value,detail,icon,onClick,tone=""}){
 return <button className={`executive-kpi ${tone}`} onClick={onClick}>
  <div className="executive-kpi-icon"><i className={`bi ${icon}`}/></div>
  <div><span>{label}</span><strong>{value}</strong><small>{detail}</small></div>
  <i className="bi bi-arrow-up-right"/>
 </button>;
}

export default function AdminDashboard(){
 const [data,setData]=useState(null);
 const [branches,setBranches]=useState([]);
 const [branchId,setBranchId]=useState("");
 const [loading,setLoading]=useState(true);
 const navigate=useNavigate();

 const load=async()=>{
  setLoading(true);
  try{
   setData(await getDashboardSummary(
    branchId?{branch_id:branchId}:{}
   ));
  }catch(error){
   toast.error(
    error?.response?.data?.detail||
    "Dashboard information could not be loaded."
   );
  }finally{
   setLoading(false);
  }
 };

 useEffect(()=>{
  getBranches().then(setBranches).catch(()=>setBranches([]));
 },[]);
 useEffect(()=>{load()},[branchId]);

 const modules=data?.visible_modules||{};
 const summary=data?.summary||{};
 const finance=data?.finance;
 const commissions=data?.commissions;
 const risk=data?.risk;
 const currency=data?.currency||{code:"",symbol:""};

 const cards=useMemo(()=>{
  const result=[];
  if(modules.policies){
   result.push({
    label:"Active policies",
    value:summary.active_policies||0,
    detail:`${summary.total_policies||0} total policies`,
    icon:"bi-shield-check",
    route:"/policies",
   });
   result.push({
    label:"Members covered",
    value:summary.total_members||0,
    detail:"Active policy members",
    icon:"bi-people",
    route:"/members",
   });
  }
  if(modules.proposals){
   result.push({
    label:"Pending proposals",
    value:summary.pending_proposals||0,
    detail:"Awaiting action",
    icon:"bi-hourglass-split",
    route:"/proposals",
    tone:summary.pending_proposals?"warning":"",
   });
  }
  if(modules.claims){
   result.push({
    label:"Open claims",
    value:summary.pending_claims||0,
    detail:"Not yet finalised",
    icon:"bi-heart-pulse",
    route:"/claims",
    tone:summary.pending_claims?"danger":"",
   });
  }
  if(finance){
   result.push({
    label:"Receipted this month",
    value:money(finance.collections_month,currency),
    detail:`${money(finance.collections_today,currency)} receipted today`,
    icon:"bi-receipt",
    route:"/finance",
   });

   result.push({
    label:"Collected today",
    value:money(finance.collected_today,currency),
    detail:"Cash physically collected / found",
    icon:"bi-cash-stack",
    route:"/expenditure",
   });

   result.push({
    label:"Cash used today",
    value:money(finance.spent_today,currency),
    detail:`${money(finance.spent_month,currency)} spent this month`,
    icon:"bi-wallet2",
    route:"/expenditure",
   });

   result.push({
    label:"Funds remaining",
    value:money(finance.funds_remaining_today,currency),
    detail:"Opening + collected - expenditure",
    icon:"bi-safe2",
    route:"/expenditure",
   });
   result.push({
    label:"Operating result",
    value:money(finance.operating_result_month,currency),
    detail:`${money(finance.operating_costs_month,currency)} total costs`,
    icon:"bi-calculator",
    route:"/finance",
    tone:Number(finance.operating_result_month)<0?"danger":"",
   });
   result.push({
    label:"Outstanding arrears",
    value:money(finance.arrears_total,currency),
    detail:`${summary.policies_in_arrears||0} affected policies`,
    icon:"bi-exclamation-diamond",
    route:"/finance",
    tone:Number(finance.arrears_total)?"danger":"",
   });
  }else if(modules.underwriting){
   result.push({
    label:"Policies in arrears",
    value:summary.policies_in_arrears||0,
    detail:"Risk follow-up required",
    icon:"bi-exclamation-triangle",
    route:"/policies",
    tone:summary.policies_in_arrears?"warning":"",
   });
  }
  if(commissions){
   result.push({
    label:"Commission outstanding",
    value:money(commissions.outstanding,currency),
    detail:`${money(commissions.paid_month,currency)} paid this month`,
    icon:"bi-person-check",
    route:"/commissions",
   });
  }
  return result;
 },[data]);

 if(loading&&!data){
  return <section className="executive-dashboard">
   <div className="executive-loading"><i className="bi bi-arrow-repeat"/><span>Preparing your workspace...</span></div>
  </section>;
 }

 return <section className="executive-dashboard">
  <header className="executive-welcome">
   <div className="welcome-copy">
    <span>{data?.dashboard_profile||"STAFF"} WORKSPACE</span>
    <h1>{greeting()}, {data?.welcome_name||"there"} <b>👋</b></h1>
    <p>Here is the live operational picture for <strong>{data?.scope_label||"your branch"}</strong>.</p>
   </div>
   <div className="welcome-actions">
    {data?.can_select_branch&&<label>
     <span>View branch</span>
     <select value={branchId} onChange={event=>setBranchId(event.target.value)}>
      <option value="">All active branches</option>
      {branches.map(branch=><option value={branch.id} key={branch.id}>{branch.name}</option>)}
     </select>
    </label>}
    <button onClick={load}><i className="bi bi-arrow-clockwise"/>Refresh</button>
    <div className="welcome-clock"><span>Last updated</span><strong>{data?.generated_at?new Date(data.generated_at).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}):"Now"}</strong></div>
   </div>
  </header>

  <div className={`executive-kpi-grid count-${Math.min(cards.length,6)}`}>
   {cards.map(card=><Kpi key={card.label} {...card} onClick={()=>navigate(card.route)}/>)}
  </div>

  {modules.management&&finance&&<div className="executive-primary-grid">
   <section className="executive-panel finance-chart-panel">
    <header><div><span>FINANCIAL PERFORMANCE</span><h3>Premium collections</h3></div><div><strong>{money(finance.collections_month,currency)}</strong><small>This month</small></div></header>
    <BarChart rows={data.collection_trend||[]} currency={currency}/>
   <footer>
     <div><span>Billed</span><strong>{money(finance.billed_month,currency)}</strong></div>
     <div><span>Collected</span><strong>{money(finance.collections_month,currency)}</strong></div>
     <div><span>Outstanding bills</span><strong>{finance.outstanding_bills||0}</strong></div>
    </footer>
    <div className="dashboard-cost-breakdown">
     <div><span>General expenses</span><strong>{money(finance.expenses_month,currency)}</strong></div>
     <div><span>Commission cost</span><strong>{money(finance.commission_cost_month,currency)}</strong></div>
     <div><span>Verified EcoSure obligation</span><strong>{money(finance.ecosure_cost_month,currency)}</strong></div>
     <div className="review"><span>EcoSure pending review ({finance.ecosure_review_rows_month||0})</span><strong>{money(finance.ecosure_review_cost_month,currency)}</strong><small>Shown separately; not deducted from operating result.</small></div>
     <div><span>Claim payouts</span><strong>{money(finance.claim_payout_cost_month,currency)}</strong></div>
     <div><span>Claim services</span><strong>{money(finance.claim_service_cost_month,currency)}</strong></div>
     <div className={Number(finance.operating_result_month)<0?"negative":""}><span>Operating result</span><strong>{money(finance.operating_result_month,currency)}</strong></div>
    </div>
   </section>
   <section className="executive-panel action-panel">
    <header><div><span>MANAGEMENT ATTENTION</span><h3>Action queue</h3></div><b>{data?.alerts?.length||0}</b></header>
    <div className="executive-alerts">
     {data?.alerts?.length?data.alerts.map((alert,index)=><button key={index} onClick={()=>navigate(alert.action_url)}>
      <i className={`bi ${alert.level==="HIGH"?"bi-exclamation-triangle":"bi-info-circle"}`}/>
      <div><strong>{alert.title}</strong><p>{alert.message}</p></div>
      <i className="bi bi-chevron-right"/>
     </button>):<div className="executive-empty"><i className="bi bi-check-circle"/><p>No urgent items require attention.</p></div>}
    </div>
   </section>
  </div>}

  {modules.underwriting&&risk&&<div className="risk-command">
   <section className="risk-hero">
    <div><span>RISK CONTROL</span><h2>Underwriting & portfolio exposure</h2><p>Focused risk information without finance or commission figures.</p></div>
    <i className="bi bi-shield-exclamation"/>
   </section>
   <div className="risk-grid">
    <Kpi label="Pending assessments" value={risk.pending_assessments} detail="Awaiting underwriting" icon="bi-clipboard2-pulse" onClick={()=>navigate("/proposals")}/>
    <Kpi label="Lapsed policies" value={risk.lapsed_policies} detail="Portfolio risk" icon="bi-shield-x" tone="danger" onClick={()=>navigate("/policies")}/>
    <Kpi label="Suspended policies" value={risk.suspended_policies} detail="Require review" icon="bi-pause-circle" tone="warning" onClick={()=>navigate("/policies")}/>
    <Kpi label="High exposure cases" value={risk.high_exposure_policies} detail="Lapsed or suspended with arrears" icon="bi-activity" tone="danger" onClick={()=>navigate("/policies")}/>
   </div>
  </div>}

  <div className="executive-chart-grid">
   {data?.policy_status?.length>0&&<RingChart title="Policy status" rows={data.policy_status} onClick={()=>navigate("/policies")}/>}
   {data?.proposal_status?.length>0&&<RingChart title="Proposal workflow" rows={data.proposal_status} onClick={()=>navigate("/proposals")}/>}
   {data?.claim_status?.length>0&&<RingChart title="Claims workflow" rows={data.claim_status} onClick={()=>navigate("/claims")}/>}
  </div>

  <div className="executive-secondary-grid">
   <section className="executive-panel executive-activity">
    <header><div><span>LIVE OPERATIONS</span><h3>Recent activity</h3></div>{modules.audit&&<button onClick={()=>navigate("/audit-trail")}>Open audit trail</button>}</header>
    <div>
     {data?.recent_activity?.length?data.recent_activity.map((item,index)=><button key={index} onClick={()=>navigate(item.action_url)}>
      <i className={`bi ${icons[item.type]||"bi-activity"}`}/>
      <div><strong>{item.title}</strong><p>{item.description}</p></div>
      <div><span>{item.status}</span><small>{new Date(item.occurred_at).toLocaleString()}</small></div>
     </button>):<div className="executive-empty"><p>No recent activity available for your role.</p></div>}
    </div>
   </section>

   <section className="executive-panel executive-shortcuts">
    <header><div><span>YOUR TOOLS</span><h3>Quick access</h3></div></header>
    <div>
     {modules.proposals&&<button onClick={()=>navigate("/proposals")}><i className="bi bi-file-earmark-text"/><span>Proposals</span></button>}
     {modules.policies&&<button onClick={()=>navigate("/policies")}><i className="bi bi-shield-check"/><span>Policies</span></button>}
     {modules.claims&&<button onClick={()=>navigate("/claims")}><i className="bi bi-heart-pulse"/><span>Claims</span></button>}
     {modules.finance&&<button onClick={()=>navigate("/finance")}><i className="bi bi-cash-stack"/><span>Finance</span></button>}
     {modules.commissions&&<button onClick={()=>navigate("/commissions")}><i className="bi bi-person-check"/><span>Commissions</span></button>}
     {modules.management&&<button onClick={()=>navigate("/communications")}><i className="bi bi-envelope-paper"/><span>Communications</span></button>}
     <button onClick={()=>navigate("/notifications")}><i className="bi bi-bell"/><span>Notifications <b>{data?.unread_notifications||0}</b></span></button>
    </div>
   </section>
  </div>

  {modules.management&&data?.branch_performance?.length>0&&<section className="executive-panel executive-branches">
   <header><div><span>BRANCH PERFORMANCE</span><h3>Operational comparison</h3></div></header>
   <div className="executive-branch-table">
    <div className="branch-line heading"><span>Branch</span><span>Active policies</span>{finance&&<span>Collections</span>}<span>Arrears</span><span>Proposals</span><span>Claims</span></div>
    {data.branch_performance.map(branch=><button className="branch-line" key={branch.id} onClick={()=>setBranchId(String(branch.id))}>
     <span><strong>{branch.name}</strong><small>{branch.code}</small></span>
     <span>{branch.active_policies}</span>
     {finance&&<span>{money(branch.collections_month,currency)}</span>}
     <span>{branch.arrears_policies}</span>
     <span>{branch.pending_proposals}</span>
     <span>{branch.open_claims}</span>
    </button>)}
   </div>
  </section>}
 </section>;
}
